<template>
    <span
        class="voice"
        :class="{ playable }"
        v-bind="$attrs"
        @click="voicePlay()"
    >
        <!-- Audio resource -->
        <audio preload="metadata">
            <source
                v-if="urlMp3"
                :src="urlMp3"
                type="audio/mpeg"
            >
            <source
                v-if="urlOgg"
                :src="urlOgg"
                type="audio/ogg"
            >
        </audio>

        <!-- Voice icon -->
        <span class="voice-icon d-inline-flex align-items-center">
            <!-- Play -->
            <template v-if="!voiceStatus || voiceStatus === 'ended'">
                <i class="volume-icon icon-voice"/>
            </template>

            <!-- Playing -->
            <template v-else-if="voiceStatus === 'playing'">
                <svg
                    class="eq-bar-wrapper"
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="18"
                    viewBox="0 0 14 18"
                >
                    <rect
                        class="eq-bar eq-bar--1"
                        x="0"
                        y="4"
                        width="4"
                        height="8"
                    />
                    <rect
                        class="eq-bar eq-bar--2"
                        x="5"
                        y="4"
                        width="4"
                        height="10"
                    />
                    <rect
                        class="eq-bar eq-bar--3"
                        x="10"
                        y="4"
                        width="4"
                        height="8"
                    />
                </svg>
            </template>

            <!-- Loading -->
            <template v-else-if="voiceStatus === 'loading'">
                <span class="spinner"/>
            </template>
        </span>

        <!-- Voice text content -->
        <span class="voice-content">
            <slot/>
        </span>
    </span>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
export default {
    name: 'Voice',
    props: {
        type: {
            type: String,
            default: ''
        },
        append: {
            type: String,
            default: ''
        },
        forceName: {
            type: String,
            default: ''
        },
        isComposite: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            urlMp3: null,
            urlOgg: null,
            filename: null,
            progressInterval: null,
            timeoutForMobiles: null,
            voiceStatus: null,
            firstWord: null,
            remainingContent: null,
            lessonNum: window.location.pathname.split('/').pop()
        }
    },
    computed: {
        ...mapGetters('lessonsModule', [
            'allRecordings'
        ]),

        // Get the shortened path (namespace) to the audio
        audioNamespace() {
            return (this.type === 'lesson_related') ? `${this.type}/${this.lessonNum}` : this.type
        },

        // Check if the audio is playable
        playable() {
            if (this.allRecordings && this.audioNamespace) {
                return this.allRecordings[`${this.audioNamespace}/${this.filename}`]
            }
            return false
        }
    },
    watch: {
        // When got all audio recording paths
        allRecordings() {
            // For development: If the current audio is not playable, check if it exists in another folder
            if (
                process.env.NODE_ENV === 'development'
                && this.allRecordings
                && this.audioNamespace
                && this.filename
                && !this.playable
            ) {
                let ns1 = `lesson_related/${this.lessonNum}`,
                    ns2 = `vocabulary`,
                    ns3 = `letter`,
                    resultNs
                if (this.allRecordings[`${ns1}/${this.filename}`]) {
                    resultNs = ns1
                } else if (this.allRecordings[`${ns2}/${this.filename}`]) {
                    resultNs = ns2
                } else if (this.allRecordings[`${ns3}/${this.filename}`]) {
                    resultNs = ns3
                }
                if (resultNs) {
                    setTimeout(() => {
                        console.log(`Найдена аудиозапись в другой папке: ${this.filename} -- не ${this.audioNamespace}, а ${resultNs}`)
                    }, 1500)
                }
            }
        },

        // When the voice status changes
        voiceStatus() {
            // If the voice status becomes 'playing', animate the equalizer icon
            if (this.voiceStatus === 'playing') {
                let delay1 = this.getRandomInRange(0, 2.25) / 10
                let delay2 = this.getRandomInRange(0, 2.25) / 10
                let delay3 = this.getRandomInRange(0, 2.25) / 10
                this.$el.style.setProperty('--eq-bar1-anim-delay', delay1 + 's')
                this.$el.style.setProperty('--eq-bar2-anim-delay', delay2 + 's')
                this.$el.style.setProperty('--eq-bar3-anim-delay', delay3 + 's')

                let dur1 = this.getRandomInRange(40, 65) / 100
                let dur2 = this.getRandomInRange(40, 65) / 100
                let dur3 = this.getRandomInRange(40, 65) / 100
                this.$el.style.setProperty('--eq-bar1-anim-duration', dur1 + 's')
                this.$el.style.setProperty('--eq-bar2-anim-duration', dur2 + 's')
                this.$el.style.setProperty('--eq-bar3-anim-duration', dur3 + 's')
            }
        }
    },
    mounted() {
        this.prepareContent()
        this.buildFilename()
        this.fetchAllRecordings()

        // Get the html audio element
        let audioElement = $(this.$el).find('audio')[0]

        // Voice loading and further progress bar logic (the green background)
        audioElement.onplay = () => {
            this.voiceStatus = 'loading'
            $(this.$el).addClass('loading')
            let timeoutForMobilesDuration = ($(window).outerWidth() > 992) ? 0 : 300
            this.timeoutForMobiles = setTimeout(() => {
                clearTimeout(this.timeoutForMobiles)
                this.progressInterval = setInterval(() => {
                    if (this.voiceStatus === 'playing') {
                        $(this.$el).css({
                            'background-size': audioElement.currentTime * 100 / audioElement.duration + '%'
                        })
                    }
                }, 15)
            }, timeoutForMobilesDuration)
        }

        // Sync the voice status to 'playing'
        audioElement.onplaying = () => {
            this.voiceStatus = 'playing'
            $('.voice.ended').removeClass('ended')
            $(this.$el).removeClass('loading').addClass('playing')
        }

        // Remove the status classes, timers and reset the audio time
        audioElement.onpause = () => {
            this.voiceStatus = 'ended'
            $(this.$el).removeClass('loading playing')
            $(this.$el).find('.eq-bar-wrapper').first().addClass('d-none')
            $(this.$el).find('.volume-icon').first().removeClass('d-none')
            clearInterval(this.progressInterval)
            clearTimeout(this.timeoutForMobiles)
            if (
                audioElement.currentTime === audioElement.duration // if the end has reached
                && !$('.voice.loading').length // if there is no another loading audio
                && !$('.voice.playing').length // if there is no another playing audio
            ) {
                $(this.$el).addClass('ended')
            } else {
                $(this.$el).css('background-size', '')
            }
            audioElement.currentTime = 0
        }
    },
    methods: {
        ...mapActions('lessonsModule', [
            'fetchAllRecordings'
        ]),

        // Get a random value within a limited range (for equalizer icon animation)
        getRandomInRange(min, max) {
            return Math.random() * (max - min) + min
        },

        // Wrap the first letter into a separate block along with the icon so that line breaks worked fine as well
        prepareContent() {
            let $voiceContent = $(this.$el).find('.voice-content')
            this.wrapFirstLetter($voiceContent)
        },

        // The wrapper function itself (recursive)
        wrapFirstLetter($el) {
            // If there is no content
            if (!$el[0].innerHTML) {
                return null
            }

            // If the content starts with another child element
            if ($el[0].innerHTML.indexOf($el.children()[0]?.outerHTML) === 0) {
                return this.wrapFirstLetter($el.children())
            }

            // Pop the first letter from the content
            let firstLetter = $el[0].innerHTML.substr(0, 1)
            $el[0].innerHTML = $el[0].innerHTML.substr(1)

            // Wrap the first letter along with the icon
            let $firstLetterWrap = $("<span class='position-relative whitespace-nowrap'>")
            let $voiceIcon = $(this.$el).find('.voice-icon')[0]
            $firstLetterWrap.append($voiceIcon, firstLetter)

            // Apply the vue data attribute to the inner span element (for styling)
            $.each($el.prop('attributes'), function() {
                if (this.name.startsWith('data-v-')) {
                    $firstLetterWrap.attr(this.name, this.value)
                }
            })

            // Prepend the first letter wrapper to the content
            $el.first().prepend($firstLetterWrap)
        },

        // Build a file name for the audio based on its text content
        buildFilename() {
            let slotContent = $(this.$el).find('.voice-content').text()
            if (this.forceName) {
                this.filename = this.forceName
            } else {
                this.filename = slotContent.replaceAll(/[\/:*?"<>|]/gmui, '').replaceAll(/\u00a0+/gmui, ' ')
                if (!this.isComposite) {
                    this.filename = this.filename.replaceAll(/-/gmui, '')
                }
                this.filename = this.filename.substring(0, 40)
            }
            if (this.append) {
                this.filename += '_' + this.append // addition to name
            }
        },

        // Build url based on namespace and other parameters
        buildUrl() {
            this.urlMp3 =
                `/audio/mp3/`// main audio folder
                + `${this.audioNamespace}/` // namespace of the audio
                + this.filename // name
                + '.mp3' // extension
            // this.urlMp3 = '/audio/mp3/lesson_related/1/vite.mp3' // TODO: убрать

            this.urlOgg =
                `/audio/ogg/`// main audio folder
                + `${this.audioNamespace}/` // namespace of the audio
                + this.filename // name
                + '.ogg' // extension
        },

        // Load and play the audio
        voicePlay() {
            if ($(this.$el).hasClass('playable')) {
                let audioElement = $(this.$el).find('audio')[0]

                // Build url if it doesn't exist
                if (!this.urlMp3) {
                    this.buildUrl()
                    audioElement.load()
                }

                // Stop all audios and reset progress bars
                $('.voice.loading, .voice.playing, .voice.ended').each(function () {
                    $(this).find('audio')[0]?.pause()
                    // Remove the green background, because the audio was paused not due to its end reaching
                    $(this).removeClass('ended').css('background-size', '')
                })

                // Play the current audio
                audioElement.play().catch((e) => {
                    console.log(e)
                })
            }
        },
    },
}
</script>

<style scoped lang="scss">
.voice {
    background: linear-gradient(90deg, #d0ffc6 100%, transparent 100%) no-repeat;
    background-size: 0;
    // color: #000;
    transition: .05s cubic-bezier(0, 0.5, 0.5, 1);
    i {
        display: none;
    }
    &, span {
        font-size: inherit;
    }
    &.playable {
        padding: 2px;
        margin-left: 21px;
        cursor: pointer;
        line-height: 25px;
        i {
            display: inline;
            width: 20px;
            color: #000a;
            font-size: .95rem;
            transition: color .15s ease;
        }
        &:hover {
            i {
                color: #000;
            }
        }
        .voice-icon {
            position: absolute;
            top: 0;
            left: -21px;
            height: 100%;
            text-align: left;
            .volume-icon {
                position: relative;
                top: 1px;
            }
        }
    }
    &.ended {
        background-size: 100% !important;
    }
}
p .voice {
    line-height: 27px;
}

// EQUALIZER
.eq-bar-wrapper {
    position: relative;
    top: -2px;
    left: 1px;
    .eq-bar {
        transform: scale(1, -1) translate(0, -25px);
        &.eq-bar--1 {
            animation-delay: var(--eq-bar1-anim-delay);
            animation-duration: var(--eq-bar1-anim-duration);
            animation-iteration-count: infinite;
            animation-name: short-eq;
        }
        &.eq-bar--2 {
            animation-delay: var(--eq-bar2-anim-delay);
            animation-duration: var(--eq-bar2-anim-duration);
            animation-iteration-count: infinite;
            animation-name: tall-eq;
        }
        &.eq-bar--3 {
            animation-delay: var(--eq-bar3-anim-delay);
            animation-duration: var(--eq-bar3-anim-duration);
            animation-iteration-count: infinite;
            animation-name: short-eq;
        }
    }
}
@keyframes short-eq {
    0% { height: 8px }
    50% { height: 14px }
    100% { height: 8px }
}
@keyframes tall-eq {
    0% { height: 10px }
    50% { height: 18px }
    100% { height: 10px }
}
</style>
